import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Flame, Download, Play, User, Target, ExternalLink } from 'lucide-react';

type ContentType = 'video' | 'profile' | 'dare';

interface DeepLinkRedirectProps {
    type: ContentType;
}

const contentConfig = {
    video: {
        icon: Play,
        title: 'Watch this video',
        description: 'Someone shared a video with you on DareUp!',
        ctaText: 'Watch in App',
    },
    profile: {
        icon: User,
        title: 'View this profile',
        description: 'Check out this creator on DareUp!',
        ctaText: 'View in App',
    },
    dare: {
        icon: Target,
        title: 'Accept this dare',
        description: 'Someone dared you to try this challenge!',
        ctaText: 'Open in App',
    },
};

export default function DeepLinkRedirect({ type }: DeepLinkRedirectProps) {
    const { id } = useParams<{ id: string }>();
    const [attemptedOpen, setAttemptedOpen] = useState(false);
    const [showFallback, setShowFallback] = useState(false);

    const config = contentConfig[type];
    const Icon = config.icon;

    // Deep link URL for the app
    const deepLinkUrl = `dareup://${type}/${id}`;

    // App store URLs
    const appStoreUrl = 'https://apps.apple.com/app/dareup/id123456789'; // TODO: Update with real App Store ID
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.dareup.app';

    useEffect(() => {
        // Try to open the app immediately
        const tryOpenApp = () => {
            setAttemptedOpen(true);

            // Create a hidden iframe to try opening the app
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = deepLinkUrl;
            document.body.appendChild(iframe);

            // Also try window.location as fallback
            setTimeout(() => {
                window.location.href = deepLinkUrl;
            }, 100);

            // Show fallback after a delay if the app didn't open
            setTimeout(() => {
                setShowFallback(true);
                document.body.removeChild(iframe);
            }, 2500);
        };

        tryOpenApp();
    }, [deepLinkUrl]);

    const handleOpenApp = () => {
        window.location.href = deepLinkUrl;
        setTimeout(() => {
            setShowFallback(true);
        }, 1500);
    };

    const handleDownload = () => {
        // Detect platform and redirect to appropriate store
        const userAgent = navigator.userAgent.toLowerCase();
        if (/iphone|ipad|ipod/.test(userAgent)) {
            window.location.href = appStoreUrl;
        } else if (/android/.test(userAgent)) {
            window.location.href = playStoreUrl;
        } else {
            // Desktop - show both options or default to App Store
            window.location.href = appStoreUrl;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl mb-4 shadow-lg shadow-orange-500/30">
                        <Flame className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-white">DareUp</h1>
                </div>

                {/* Content Card */}
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 shadow-xl">
                    {/* Content Type Icon */}
                    <div className="flex justify-center mb-4">
                        <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center">
                            <Icon className="w-8 h-8 text-orange-500" />
                        </div>
                    </div>

                    <h2 className="text-xl font-bold text-white text-center mb-2">{config.title}</h2>
                    <p className="text-gray-400 text-center mb-6">{config.description}</p>

                    {/* Loading State */}
                    {attemptedOpen && !showFallback && (
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center gap-2 text-gray-300">
                                <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                                <span>Opening DareUp...</span>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="space-y-3">
                        <button
                            onClick={handleOpenApp}
                            className="w-full py-4 px-6 bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-red-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30"
                        >
                            <ExternalLink className="w-5 h-5" />
                            {config.ctaText}
                        </button>

                        {showFallback && (
                            <button
                                onClick={handleDownload}
                                className="w-full py-4 px-6 bg-gray-700 text-white font-semibold rounded-xl hover:bg-gray-600 transition-all duration-200 flex items-center justify-center gap-2"
                            >
                                <Download className="w-5 h-5" />
                                Download DareUp
                            </button>
                        )}
                    </div>

                    {/* App Store Badges */}
                    {showFallback && (
                        <div className="mt-6 pt-6 border-t border-gray-700/50">
                            <p className="text-gray-500 text-sm text-center mb-4">Don't have the app yet?</p>
                            <div className="flex justify-center gap-4">
                                <a href={appStoreUrl} className="opacity-80 hover:opacity-100 transition-opacity">
                                    <img
                                        src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                                        alt="Download on the App Store"
                                        className="h-10"
                                    />
                                </a>
                                <a href={playStoreUrl} className="opacity-80 hover:opacity-100 transition-opacity">
                                    <img
                                        src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                                        alt="Get it on Google Play"
                                        className="h-10"
                                    />
                                </a>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <p className="text-gray-500 text-sm text-center mt-6">The ultimate dare challenge app</p>
            </div>
        </div>
    );
}
